import { registerQuery } from 'data-fetching-client';
import { USE_CURRENCY_CHIRP_V2 } from './featureFlags';
import { getCurrencyRequestRpc, getPublicCurrencyResponseRpc } from 'growth-monetization-service-types';
import http from 'hub-http/clients/apiClient';
import noAuthHttp from 'hub-http/clients/noAuthApiClient';
import { getPublicCurrencyV2Rpc } from 'pricing-pages-api/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc';
import { getCurrencyV2Rpc } from 'pricing-pages-api/__generated__/chirp/com/hubspot/buying/rpc/ProductRpc';
import { createRpcClientV2 } from 'rpc-client-utils';
import { getEarlyRequestResult } from '../utils/earlyRequest';
import { getCurrencyStoreResponse } from './dataTransformer';
// ******************* Public ***********************
const rpcClientNoAuth = createRpcClientV2({
  hubHttpClient: noAuthHttp
});

// CHIRP V2 Query
export const GET_PUBLIC_CURRENCIES_V2 = registerQuery({
  fieldName: 'currencies',
  args: ['earlyRequestName', 'preferredCountryCode', 'browserLocale'],
  fetcher({
    earlyRequestName,
    browserLocale,
    preferredCountryCode
  }) {
    return getEarlyRequestResult(earlyRequestName, () => rpcClientNoAuth.call(getPublicCurrencyV2Rpc, {
      request: {
        browserLocale,
        // Type cast necessary: CHIRP expects specific CountryCode type, but we receive
        // string | undefined from various sources. Early requester validates at call site,
        // and backend validates the actual value. This cast bridges the type boundary.
        preferredCountryCode: preferredCountryCode
      }
    })).then(response => {
      // Type cast necessary: Using structural typing to handle both CHIRP V2 and REST responses.
      // UnifiedCurrencyResponse accepts both null (REST) and undefined (CHIRP) for optional fields.
      // Type guards (isCurrencyCodeString, isCountryCodeString) provide runtime validation.
      return getCurrencyStoreResponse(response);
    }).catch(error => {
      console.error('Failed to fetch public currencies (V2):', error);
      throw error;
    });
  }
});

// REST Fallback Query
export const GET_PUBLIC_CURRENCIES_REST = registerQuery({
  fieldName: 'currencies',
  args: ['earlyRequestName', 'preferredCountryCode', 'browserLocale'],
  fetcher({
    earlyRequestName,
    browserLocale,
    preferredCountryCode
  }) {
    return getEarlyRequestResult(earlyRequestName, () => rpcClientNoAuth.call(getPublicCurrencyResponseRpc, {
      pathParameters: {
        browserLocale
      },
      queryParameters: {
        preferredCountryCode
      }
    })).then(response => {
      // Type cast necessary: Using structural typing to handle both CHIRP V2 and REST responses.
      // UnifiedCurrencyResponse accepts both null (REST) and undefined (CHIRP) for optional fields.
      // Type guards (isCurrencyCodeString, isCountryCodeString) provide runtime validation.
      return getCurrencyStoreResponse(response);
    }).catch(error => {
      console.error('Failed to fetch public currencies (REST):', error);
      throw error;
    });
  }
});

// Export with feature flag check
export const GET_PUBLIC_CURRENCIES = USE_CURRENCY_CHIRP_V2 ? GET_PUBLIC_CURRENCIES_V2 : GET_PUBLIC_CURRENCIES_REST;

// ******************* In-App ***********************
const rpcClientAuth = createRpcClientV2({
  hubHttpClient: http
});

// CHIRP V2 Query
export const GET_IN_APP_CURRENCIES_V2 = registerQuery({
  fieldName: 'currencies',
  args: ['earlyRequestName'],
  fetcher({
    earlyRequestName
  }) {
    return getEarlyRequestResult(earlyRequestName, () => rpcClientAuth.call(getCurrencyV2Rpc, {})).then(response => {
      // Type cast necessary: Using structural typing to handle both CHIRP V2 and REST responses.
      // UnifiedCurrencyResponse accepts both null (REST) and undefined (CHIRP) for optional fields.
      // Type guards (isCurrencyCodeString, isCountryCodeString) provide runtime validation.
      return getCurrencyStoreResponse(response);
    }).catch(error => {
      console.error('Failed to fetch in-app currencies (V2):', error);
      throw error;
    });
  }
});

// REST Fallback Query
export const GET_IN_APP_CURRENCIES_REST = registerQuery({
  fieldName: 'currencies',
  args: ['earlyRequestName'],
  fetcher({
    earlyRequestName
  }) {
    return getEarlyRequestResult(earlyRequestName, () => rpcClientAuth.call(getCurrencyRequestRpc, {})).then(response => {
      // Type cast necessary: Using structural typing to handle both CHIRP V2 and REST responses.
      // UnifiedCurrencyResponse accepts both null (REST) and undefined (CHIRP) for optional fields.
      // Type guards (isCurrencyCodeString, isCountryCodeString) provide runtime validation.
      return getCurrencyStoreResponse(response);
    }).catch(error => {
      console.error('Failed to fetch in-app currencies (REST):', error);
      throw error;
    });
  }
});

// Export with feature flag check
export const GET_IN_APP_CURRENCIES = USE_CURRENCY_CHIRP_V2 ? GET_IN_APP_CURRENCIES_V2 : GET_IN_APP_CURRENCIES_REST;